import moment from 'moment';
import React from 'react';
import styledComponent from '../../utils/styledComponent';
import { STATUS_STYLES, DEFAULT_STATUS } from '../PalletOperations/StatusBadge';

const QRCode = require('qrcode.react');

const getStatusColor = (status) => {
  if (!status) return DEFAULT_STATUS.background;
  const normalized = status.toUpperCase().replace(/[-\s]/g, '_');
  return (STATUS_STYLES[normalized] || DEFAULT_STATUS).background;
};

const StyledDiv = styledComponent('div', {
  '& .pallet-slip': {
    width: '100%',
    marginBottom: '30px',
    pageBreakInside: 'avoid',
    border: '3px solid #000',
    background: '#fff',
  },
  '& .pallet-slip-label': {
    color: '#000',
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #000',
  },
  '& .pallet-status-badge': {
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 12px',
    borderRadius: '4px',
    letterSpacing: '1px',
  },
  '& .pallet-slip-header': {
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '3px solid #000',
  },
  '& .pallet-number': {
    fontSize: '72px',
    fontWeight: '900',
    letterSpacing: '5px',
    lineHeight: 1,
    color: '#000',
  },
  '& .pallet-total': {
    fontSize: '24px',
    fontWeight: '500',
    color: '#555',
  },
  '& .market-name': {
    fontSize: '28px',
    fontWeight: '700',
    textAlign: 'right',
    color: '#000',
  },
  '& .linehaul-info': {
    fontSize: '16px',
    color: '#555',
    textAlign: 'right',
  },
  '& .pallet-slip-info-table': {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      background: '#fff',
      color: '#000',
      border: '1px solid #000',
      padding: '8px 16px',
      fontSize: '12px',
      fontWeight: '900',
      textTransform: 'uppercase',
      textAlign: 'left',
    },
    '& td': {
      border: '1px solid #000',
      padding: '10px 16px',
      fontSize: '20px',
      fontWeight: '500',
      background: '#fff',
    },
  },
  '& .pallet-slip-body': {
    display: 'flex',
    padding: '20px',
    background: '#fff',
    justifyContent: 'space-between',
  },
  '& .pallet-slip-left': {
    display: 'flex',
    flex: 1,
  },
  '& .pallet-slip-qr': {
    width: '140px',
    height: '140px',
    border: '2px solid #000',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#fff',
  },
  '& .pallet-slip-qr-label': {
    fontSize: '11px',
    fontWeight: '600',
    textAlign: 'center',
    marginTop: '6px',
    color: '#000',
    wordBreak: 'break-all',
    maxWidth: '140px',
  },
  '& .pallet-slip-details': {
    flex: 1,
  },
  '& .detail-row': {
    display: 'flex',
    marginBottom: '12px',
  },
  '& .detail-label': {
    fontSize: '14px',
    fontWeight: '700',
    color: '#555',
    width: '100px',
    textTransform: 'uppercase',
  },
  '& .detail-value': {
    fontSize: '18px',
    fontWeight: '500',
    color: '#000',
  },
  '& .pallet-slip-routes': {
    padding: '15px 20px',
    borderTop: '2px solid #000',
    margin: '0',
  },
  '& .routes-label': {
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    color: '#000',
    marginBottom: '8px',
  },
  '& .route-tag': {
    display: 'inline-block',
    background: '#fff',
    border: '2px solid #000',
    padding: '4px 12px',
    marginRight: '8px',
    marginBottom: '8px',
    fontSize: '14px',
    borderRadius: '4px',
    color: '#000',
    fontWeight: '600',
  },
  '& .route-tag--split': {
    border: '2px solid #2a5fa5',
    color: '#2a5fa5',
  },
  '& .split-badge': {
    display: 'inline-block',
    background: '#2a5fa5',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '800',
    padding: '2px 6px',
    borderRadius: '3px',
    marginRight: '6px',
    verticalAlign: 'middle',
    letterSpacing: '0.5px',
  },
  '@media print': {
    '& .pallet-slip': {
      maxWidth: '100%',
      width: '100%',
      pageBreakInside: 'avoid',
      pageBreakAfter: 'always',
    },
  },
});

/* eslint-disable react/prop-types */
const PalletSlip = ({
  pallet,
  palletIndex,
  totalPallets,
  showStatus = true,
}) => {
  if (!pallet) {
    return null;
  }

  const {
    palletSequence,
    totalPalletsForLinehaul,
    linehaulFirstMile,
    linehaulMiddleMile,
    market,
    region,
    dropOffDate,
    toteCount,
    layerCount,
    totalItemCount,
    routes,
    qrCode,
    uniqueId,
    loadStatus,
  } = pallet;

  const statusColor = showStatus ? getStatusColor(loadStatus) : null;

  const formattedDropOffDate = dropOffDate
    ? moment(dropOffDate).format('MMM D, YYYY')
    : 'Not specified';

  const linehaulDisplay =
    linehaulFirstMile && linehaulMiddleMile
      ? `${linehaulFirstMile} → ${linehaulMiddleMile}`
      : linehaulFirstMile || 'N/A';

  const displayPalletNumber = palletSequence || palletIndex + 1;
  const displayTotalPallets = totalPalletsForLinehaul || totalPallets;

  const routesList = Array.isArray(routes) ? routes : [];

  const displayStatus = loadStatus
    ? loadStatus.replace(/_/g, ' ').toUpperCase()
    : 'PENDING';

  return (
    <StyledDiv data-test="pallet-slip">
      <div className="pallet-slip">
        <div className="pallet-slip-label">
          <span>PALLET SLIP</span>
          <span
            className="pallet-status-badge"
            style={
              statusColor
                ? { background: statusColor, color: '#fff' }
                : { background: '#888', color: '#fff' }
            }
          >
            {showStatus ? displayStatus : 'PREVIEW'}
          </span>
        </div>
        <div className="pallet-slip-header">
          <div>
            <div className="pallet-number">{displayPalletNumber}</div>
            <div className="pallet-total">{displayTotalPallets} total</div>
          </div>
          <div>
            <div className="market-name">{market || 'N/A'}</div>
            <div className="linehaul-info">{linehaulDisplay}</div>
          </div>
        </div>
        <table className="pallet-slip-info-table">
          <thead>
            <tr>
              {[
                'Linehaul',
                'Middle Mile',
                'Market',
                'Region',
                'Drop-off Date',
              ].map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{linehaulFirstMile || '—'}</td>
              <td>{linehaulMiddleMile || '—'}</td>
              <td>{market || '—'}</td>
              <td>{region || '—'}</td>
              <td>{formattedDropOffDate}</td>
            </tr>
          </tbody>
        </table>
        <div className="pallet-slip-body">
          <div className="pallet-slip-left">
            <div style={{ marginRight: '30px', flexShrink: 0 }}>
              <div className="pallet-slip-qr">
                {(() => {
                  const qrValue = qrCode || uniqueId;
                  if (qrValue) {
                    return (
                      <QRCode
                        size={120}
                        value={qrValue}
                        data-testid={qrValue}
                      />
                    );
                  }
                  return <div>No QR</div>;
                })()}
              </div>
              {(qrCode || uniqueId) && (
                <div className="pallet-slip-qr-label">{qrCode || uniqueId}</div>
              )}
            </div>
            <div className="pallet-slip-details">
              {showStatus && (
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span
                    className="detail-value"
                    style={{ color: statusColor, fontWeight: '700' }}
                  >
                    {displayStatus}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Totes</span>
                <span className="detail-value">{toteCount || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Layers</span>
                <span className="detail-value">{layerCount || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Items</span>
                <span className="detail-value">
                  {totalItemCount ? totalItemCount.toLocaleString() : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
        {routesList.length > 0 && (
          <div className="pallet-slip-routes">
            <div className="routes-label">Routes ({routesList.length})</div>
            {routesList.map((routeItem) => {
              const isSplit =
                routeItem.maxTotes != null &&
                routeItem.maxTotes > routeItem.totes;
              const toteLabel = isSplit
                ? `${routeItem.totes} of ${routeItem.maxTotes} totes`
                : `${routeItem.totes} totes`;
              return (
                <span
                  key={routeItem.route}
                  className={`route-tag${isSplit ? ' route-tag--split' : ''}`}
                >
                  {isSplit && <span className="split-badge">SPLIT</span>}
                  {routeItem.route} ({toteLabel})
                </span>
              );
            })}
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export default PalletSlip;
